import React, { useMemo, useState } from 'react';

import { Flex } from 'antd-v5';
import { DateTime } from 'luxon';
import { MdArrowBack } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'scouting-ui/lib';

import { useMBDict } from '@dict/duck/hooks';
import { sortBulkRequirements } from '@modules/bulkEntry/duck/utils/requirementUtils';
import { getVersionStatusLabel } from '@modules/bulkEntry/duck/utils/versions';
import ErrorBoundary from '@modules/core/components/ErrorBoundary';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  mbcApi,
  useGetCounselorClassesQuery,
  useGetMeritBadgeCounselorProfileQuery,
} from '@modules/meritBadgeCounselors/api';
import type {
  MBVersion,
  YouthSubmitResult,
} from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/types';
import { parseSubmitError } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/utils';
import { routeMBCDashboard } from '@modules/meritBadgeCounselors/mbcDashboard/duck/actions';
import {
  useGetMeritBadgeRequirementsQuery,
  usePostBulkYouthMeritBadgeRequirementsMutation,
} from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { useGenerateClassWorksheetMutation } from '@modules/rtk/webscript-api';
import PageContainer from '@shared/components/Layouts/PageContainer';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';
import { currentUserMIDSel } from '@user/duck/selectorsTyped';

import type { AppliedClassEntry, ClassEntryFormValues } from '../types';
import {
  type YouthIneligibility,
  buildSavePayload,
  toClassOptions,
  youthIneligibility,
} from '../utils';
import ClassEntryPage from './ClassEntryPage';

const versionLabel = (version: MBVersion) => {
  const { status, date } = getVersionStatusLabel(version);
  const luxonDate = DateTime.fromJSDate(date.toDate());

  return intl.formatMessage(
    { id: `bulkEntry.versionDropdown.${status}` },
    {
      date: luxonDate.toFormat('MMM yyyy'),
      hasDate: luxonDate.isValid,
      version: version.version,
    },
  );
};

const disabledReasonMessage = (ineligibility: YouthIneligibility) =>
  intl.formatMessage(
    { id: `page.mbcClassEntry.disabled.${ineligibility.reason}` },
    'startedVersion' in ineligibility
      ? { version: ineligibility.startedVersion }
      : undefined,
  );

function ClassEntryPageContainer() {
  const dispatch = useDispatch();
  const memberId = useSelector(currentUserMIDSel);
  const [applied, setApplied] = useState<AppliedClassEntry>();
  const [saveResults, setSaveResults] = useState<YouthSubmitResult[]>([]);

  const {
    meritBadgesById,
    response: { isLoading: isLoadingDict },
  } = useMBDict({ params: { status: 'all' } });
  const {
    data: counselorClasses,
    isLoading: isLoadingClasses,
    isError: classesFailed,
    refetch: refetchClasses,
  } = useGetCounselorClassesQuery(
    { params: { counselorMemberId: memberId } },
    { skip: !memberId },
  );
  const {
    data: mbcProfile,
    isLoading: isLoadingProfile,
    isError: profileFailed,
    refetch: refetchProfile,
  } = useGetMeritBadgeCounselorProfileQuery({ memberId }, { skip: !memberId });

  const classes = useMemo(
    () => toClassOptions(counselorClasses ?? [], meritBadgesById, versionLabel),
    [counselorClasses, meritBadgesById],
  );
  const appliedClass = classes.find(mbClass => mbClass.id === applied?.classId);

  const { data: requirementsInfo, isFetching: isFetchingRequirements } =
    useGetMeritBadgeRequirementsQuery(
      {
        meritBadgeId: appliedClass?.meritBadgeId as number,
        params: { versionId: applied?.versionId },
      },
      { skip: !appliedClass || !applied?.versionId },
    );
  const requirements = useMemo(
    () => sortBulkRequirements(requirementsInfo?.requirements ?? []),
    [requirementsInfo],
  );

  const disabledReasons = useMemo(() => {
    if (!appliedClass || !applied) return new Map<number, string>();

    const version = meritBadgesById
      .get(appliedClass.meritBadgeId)
      ?.versions.find(v => Number(v.versionId) === applied.versionId);
    const ineligibility = youthIneligibility(
      appliedClass.scouts,
      mbcProfile?.youths ?? [],
      appliedClass.meritBadgeId,
      version,
    );

    return new Map(
      [...ineligibility].map(([id, item]) => [id, disabledReasonMessage(item)]),
    );
  }, [appliedClass, applied, meritBadgesById, mbcProfile?.youths]);

  const [postRequirements, { isLoading: isSaving }] =
    usePostBulkYouthMeritBadgeRequirementsMutation();
  const [generateClassWorksheet, { isLoading: isPrinting }] =
    useGenerateClassWorksheetMutation();

  const handleApply = (next: AppliedClassEntry) => {
    setApplied(next);
    setSaveResults([]);
  };

  const handleSave = async (values: ClassEntryFormValues) => {
    if (!appliedClass || !applied) return false;

    setSaveResults([]);
    try {
      await postRequirements({
        meritBadgeId: appliedClass.meritBadgeId,
        payload: buildSavePayload({
          values,
          requirements,
          roster: appliedClass.scouts,
          versionId: applied.versionId,
          today: DateTime.now(),
        }),
      }).unwrap();

      toastService.success(
        intl.formatMessage({ id: 'page.mbcClassEntry.toasts.success' }),
      );
      // Manual because these tags key off the counselor MID, which the mutation args don't carry
      dispatch(
        mbcApi.util.invalidateTags([
          tagFactory.MBCDashboard.userDashboard(memberId),
          tagFactory.MeritBadgeCounselorProfile.mbcProfile(memberId),
        ]),
      );
      return true;
    } catch (error) {
      const results = parseSubmitError(error);
      if (results) {
        setSaveResults(results);
      } else {
        toastService.error(
          intl.formatMessage({ id: 'page.mbcClassEntry.toasts.error' }),
        );
      }
      return false;
    }
  };

  const handlePrint = async () => {
    if (!appliedClass) return;

    try {
      const blob = await generateClassWorksheet({
        memberIds: appliedClass.scouts.map(youth => youth.memberId),
        meritBadgeId: String(appliedClass.meritBadgeId),
      }).unwrap();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toastService.error(
        intl.formatMessage({ id: 'page.mbcClassEntry.toasts.printError' }),
      );
    }
  };

  return (
    <main>
      <Flex vertical gap="middle">
        <div>
          <Button
            type="link"
            onClick={() => dispatch(routeMBCDashboard())}
            icon={<MdArrowBack />}
          >
            <FormattedMessage
              id="shared.backTo"
              values={{
                name: intl.formatMessage({ id: 'page.mbcDashboard' }),
              }}
            />
          </Button>
        </div>
        <ClassEntryPage
          classes={classes}
          applied={applied}
          requirements={requirements}
          disabledReasons={disabledReasons}
          saveResults={saveResults}
          loadError={classesFailed || profileFailed}
          onRetryLoad={() => {
            refetchClasses();
            refetchProfile();
          }}
          loadingClasses={isLoadingClasses || isLoadingDict}
          loadingRequirements={isFetchingRequirements || isLoadingProfile}
          isSaving={isSaving}
          isPrinting={isPrinting}
          canPrint={Boolean(
            featureFlags.getFlag('SBL_4163_GENERATE_CLASS_WORKSHEET'),
          )}
          onApply={handleApply}
          onSave={handleSave}
          onPrint={handlePrint}
        />
      </Flex>
    </main>
  );
}

export default function ClassEntryPageWrapper() {
  if (!featureFlags.getFlag('SBL_6161_MBC_CLASS_ENTRY')) return null;

  return (
    <ErrorBoundary>
      <PageContainer>
        <ClassEntryPageContainer />
      </PageContainer>
    </ErrorBoundary>
  );
}
